import { useState } from 'react'
import { useNavigate, useLocation, Navigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { api } from '../lib/api'
import { useAuthStore, type AuthUser } from '../stores/auth'
import type { Permission } from '@b4l/shared'
import { PasswordField } from '../components/ui/PasswordField'

const LoginSchema = z.object({
  email: z.string().email('Email invalid'),
  password: z.string().min(1, 'Introdu parola'),
})

type LoginInput = z.infer<typeof LoginSchema>

interface LoginResponse {
  accessToken: string
  user: AuthUser
  permissions: Permission[]
}

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const setSession = useAuthStore((s) => s.setSession)
  const user = useAuthStore((s) => s.user)
  const [submitting, setSubmitting] = useState(false)

  const form = useForm<LoginInput>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { email: '', password: '' },
  })

  if (user) {
    const next = (location.state as { from?: string } | null)?.from ?? '/dashboard'
    return <Navigate to={next} replace />
  }

  const onSubmit = async (data: LoginInput) => {
    setSubmitting(true)
    try {
      const res = await api.post<LoginResponse>('/auth/login', data)
      setSession(res.data.accessToken, res.data.user, res.data.permissions)
      toast.success(`Bine ai venit, ${res.data.user.name}`)
      const next = res.data.user.mustChangePassword ? '/change-password' : '/dashboard'
      navigate(next, { replace: true })
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare la autentificare'
      toast.error(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex h-screen items-center justify-center bg-b4l-bg">
      <div className="w-full max-w-sm rounded-lg border border-b4l-border bg-white p-8 shadow-sm">
        <h1 className="mb-1 text-xl font-semibold text-b4l-green-dark">Boost4Life CRM</h1>
        <p className="mb-6 text-sm text-b4l-text-muted">Autentificare</p>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <div>
            <label htmlFor="email" className="mb-1 block text-xs font-medium text-b4l-text-muted">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              {...form.register('email')}
              className="w-full rounded border border-b4l-border px-3 py-2 outline-none focus:border-b4l-green"
              placeholder="nume@boost4life.ro"
            />
            {form.formState.errors.email && (
              <p className="mt-1 text-xs text-b4l-red">{form.formState.errors.email.message}</p>
            )}
          </div>
          <PasswordField
            label="Parolă"
            autoComplete="current-password"
            {...form.register('password')}
            error={form.formState.errors.password?.message}
          />
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded bg-b4l-green px-3 py-2 text-white hover:bg-b4l-green-dark disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Se autentifică…' : 'Autentificare'}
          </button>
          <Link
            to="/forgot-password"
            className="block text-center text-xs text-b4l-text-muted hover:text-b4l-green-dark"
          >
            Ai uitat parola?
          </Link>
        </form>
      </div>
    </div>
  )
}
